import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useIAConversationsStore, IAChatMessage } from '../../../../Stores/IAConversationsStore';
import { useAuthStore } from '../../../../Stores/AuthStore';
import { openaiIAProvider } from '../../../../services/iaProvider';
import { buildSystemPromptText } from '../../../../services/iaPromptBuilder';
import useCurrentSav from '../Hooks/useCurrentSav';
import { extractPromptDataFromSav } from '../../../../services/iaPromptExtractor';
import MarkdownRenderer from '../../../../Components/MarkdownRenderer';
import { IMessage } from '../../../../models/message';
import { authenticatedApi } from '../../../../Api/api';
import { getDocumentBase64Url, getDocumentDownloadUrl } from '../../../../helpers/Documents/openDocumentInBrowser';
import DocumentsSelector from '../Components/DocumentsSelector/DocumentsSelector';
import { IDocument } from '../../../../models/document';

const IAConversationPage: React.FC = () => {
  const { savId, convId } = useParams();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const gestionnaireId = user?.uuid ?? null;
  const sid = savId ? Number(savId) : null;
  const conversation = useIAConversationsStore((state) =>
    sid && gestionnaireId && convId
      ? state.getConversationsBySav(sid, gestionnaireId).find((c) => c.id === convId)
      : undefined
  );
  const { sav } = useCurrentSav();
  const addMessage = useIAConversationsStore((state) => state.addMessage);
  const setStreamingState = useIAConversationsStore((state) => state.setStreamingState);
  const [beneficiaryMessages, setBeneficiaryMessages] = useState<IMessage[] | undefined>(undefined);

  // Provider IA : toujours openai
  const iaProvider = openaiIAProvider;

  // Gestion de l'input message
  const [input, setInput] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Scroll auto
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);

  // Cache local pour éviter de retransformer le même contrat
  const [contractBase64, setContractBase64] = useState<string | null>(null);

  // Ajout de l'état pour les documents sélectionnés
  const [selectedDocuments, setSelectedDocuments] = useState<IDocument[]>([]);

  // Fonction pour scroller en bas
  const scrollToBottom = useCallback((behavior: 'smooth' | 'instant' = 'smooth') => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior });
    }
  }, []);

  // Scroll auto lors de l'envoi d'un message ou streaming
  useEffect(() => {
    if (autoScroll && (isLoading || conversation?.isStreaming)) {
      scrollToBottom();
    }
  }, [conversation?.messages.length, conversation?.streamingContent, isLoading, conversation?.isStreaming, autoScroll, scrollToBottom]);

  // Scroll en bas à l'arrivée sur la page ou quand la conversation change
  useEffect(() => {
    scrollToBottom('instant');
  }, [conversation?.id, scrollToBottom]);

  // Désactive l'auto-scroll si l'utilisateur scroll vers le haut, le réactive si il revient en bas
  const handleScroll = () => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const threshold = 40; // px
    const atBottom = container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
    setAutoScroll(atBottom);
  };

  // Chargement des messages bénéficiaire
  useEffect(() => {
    const fetchMessages = async () => {
      if (!sid) return;
      try {
        const res = await authenticatedApi.get(`/sav/${sid}/messages/beneficiary`, { params: { total: 20 } });
        setBeneficiaryMessages(res.data?.nodes || []);
      } catch (e) {
        setBeneficiaryMessages([]);
      }
    };
    fetchMessages();
  }, [sid]);

  // Génération du system prompt contextuel (pour affichage et envoi)
  let systemPrompt = '';
  if (sav) {
    const promptData = extractPromptDataFromSav(sav, beneficiaryMessages);
    systemPrompt = buildSystemPromptText(promptData);
  }

  // Fonction utilitaire pour obtenir le PDF du contrat en base64 via l'API sécurisée
  const getContractBase64 = async (contractDoc) => {
    if (!contractDoc) return null;
    if (contractBase64) return contractBase64; // déjà transformé
    try {
      // Récupère l'URL sécurisée avec token
      const url = await getDocumentBase64Url(contractDoc);
      if (!url) throw new Error('Impossible de générer l\'URL du contrat');
      const res = await fetch(url);
      if (!res.ok) throw new Error('Erreur lors de la récupération du PDF en base64');
      const data = await res.json();
      if (!data.base64) throw new Error('PDF base64 manquant dans la réponse');
      setContractBase64(data.base64);
      return data.base64;
    } catch (e) {
      console.error('Erreur lors de la récupération du contrat PDF en base64 :', e);
      return null;
    }
  };

  // Nouvelle fonction utilitaire pour obtenir le base64 d'un document SAV
  const getDocumentBase64 = async (doc: IDocument) => {
    try {
      const url = await getDocumentBase64Url(doc);
      if (!url) throw new Error('Impossible de générer l\'URL du document');
      const res = await fetch(url);
      if (!res.ok) throw new Error('Erreur lors de la récupération du PDF en base64');
      const data = await res.json();
      if (!data.base64) throw new Error('PDF base64 manquant dans la réponse');
      return data.base64;
    } catch (e) {
      console.error('Erreur lors de la récupération du document PDF en base64 :', e);
      return null;
    }
  };

  // Remet l'auto-scroll à true lors de l'envoi d'un message
  const handleSend = async () => {
    if (conversation && input.trim() && !isLoading) {
      setAutoScroll(true);

      // Récupérer le base64 de chaque document sélectionné
      const attachmentsWithBase64: {
        id: number;
        label: string;
        type: 'pdf' | 'image';
        base64: string;
      }[] = [];
      for (const doc of selectedDocuments) {
        const base64 = await getDocumentBase64(doc);
        if (base64) {
          attachmentsWithBase64.push({
            id: doc.id,
            label: doc.label,
            type: doc.path?.toLowerCase().includes('.pdf') ? 'pdf' as const : 'image' as const,
            base64: base64
          });
        }
      }

      addMessage(conversation.id, {
        role: 'user',
        content: input.trim(),
        attachments: attachmentsWithBase64.length > 0 ? attachmentsWithBase64 : undefined,
      });
      setInput('');
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
      setIsLoading(true);
      setStreamingState(conversation.id, true, '');
      let last = '';
      const messages: any[] = [];

      // Ajout du contrat en base64 en premier (contexte initial) s'il existe
      if (sav?.contract?.document) {
        const base64 = await getContractBase64(sav.contract.document);
        if (base64) {
          messages.push({ role: 'user', content: 'Voici le contrat du SAV, si tu le trouves utile pour répondre à la question. Si je fais référence au contrat dans mes prochaines questions, tu dois le lire et le comprendre.' });
          messages.push({
            role: 'user',
            content: [
              {
                type: 'file',
                file: {
                  filename: 'contrat.pdf',
                  file_data: `${base64}`
                }
              }
            ]
          });
        }
      }

      // Ajout de l'historique des messages avec leurs attachments
      messages.push(...conversation.messages.map(m => {
        const baseMessage = { role: m.role, content: m.content };
        // Si le message a des attachments, les ajouter aussi pour l'IA
        if (m.role === 'user' && m.attachments && m.attachments.length > 0) {
          const attachmentMessages: any[] = [];
          m.attachments.forEach(attachment => {
            if (attachment.base64) {
              const isImage = attachment.base64.startsWith('data:image/');
              if (isImage) {
                attachmentMessages.push({
                  role: 'user',
                  content: [
                    {
                      type: "image_url",
                      image_url: {
                        url: attachment.base64,
                      },
                    }
                  ]
                });
              } else {
                attachmentMessages.push({
                  role: 'user',
                  content: [
                    {
                      type: 'file',
                      file: {
                        filename: attachment.label || `document_${attachment.id}.pdf`,
                        file_data: attachment.base64
                      }
                    }
                  ]
                });
              }
            }
          });
          return [baseMessage, ...attachmentMessages];
        }
        return baseMessage;
      }).flat());

      // Ajout des documents SAV sélectionnés (nouveau message)
      if (selectedDocuments.length > 0) {
        for (const attachment of attachmentsWithBase64) {
          if (attachment.base64) {
            // Vérifier si c'est une image en analysant le préfixe data URI
            const isImage = attachment.base64.startsWith('data:image/');

            if (isImage) {
              // Format pour les images
              messages.push({
                role: 'user',
                content: [
                  {
                    type: "image_url",
                    image_url: {
                      url: attachment.base64, // base64 contient déjà le préfixe data:image/...
                    },
                  }
                ]
              });
            } else {
              // Format pour les PDFs
              messages.push({
                role: 'user',
                content: [
                  {
                    type: 'file',
                    file: {
                      filename: attachment.label || `document_${attachment.id}.pdf`,
                      file_data: attachment.base64
                    }
                  }
                ]
              });
            }
          }
        }
        // Après envoi, on vide la sélection
        setSelectedDocuments([]);
      }

      // Ajout du message texte utilisateur
      messages.push({ role: 'user', content: input.trim() });

      for await (const partial of iaProvider.sendMessageStream(messages, systemPrompt)) {
        last += partial;
        setStreamingState(conversation.id, true, last);
      }
      setStreamingState(conversation.id, false, '');
      addMessage(conversation.id, {
        role: 'assistant',
        content: last,
      });
      setIsLoading(false);
    }
  };
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSend();
  };

  if (!conversation) {
    return (
      <div className="tw-w-full tw-h-screen tw-flex tw-items-center tw-justify-center tw-bg-gray-50">
        <div className="tw-text-gray-500 tw-italic tw-mb-4">Conversation introuvable.</div>
        <button
          className="tw-bg-blue-600 tw-text-white tw-px-4 tw-py-2 tw-rounded-lg tw-shadow-sm tw-font-medium hover:tw-bg-blue-700 tw-transition tw-ml-4"
          onClick={() => navigate(`/sav/${savId}/ia`)}
        >
          Retour à la liste
        </button>
      </div>
    );
  }

  return (
    <div className="tw-bg-gray-50 tw-flex tw-flex-col tw-h-full">
      {/* Header */}
      <div className="tw-p-4 tw-border-b tw-border-gray-200 tw-flex tw-justify-between tw-items-center tw-bg-white tw-z-10">
        <div>
          <div className="tw-font-semibold tw-text-lg tw-text-gray-800">{conversation.title}</div>
          <div className="tw-text-xs tw-text-gray-400">Créée le {new Date(conversation.createdAt).toLocaleString()}</div>
        </div>
        <span className="tw-text-xs tw-text-gray-500">{conversation.messages.length} messages</span>
        <button
          className="tw-ml-4 tw-text-blue-600 hover:tw-underline tw-text-sm"
          onClick={() => navigate(`/sav/${savId}/ia`)}
        >
          ← Retour à la liste
        </button>
      </div>
      {/* Messages */}
      <div
        ref={messagesContainerRef}
        className="tw-flex-1 tw-overflow-y-auto tw-p-4 tw-space-y-4 tw-bg-gray-50"
        onScroll={handleScroll}
        style={{ minHeight: 0 }}
      >
        {/* Bloc prompt system au début de la conversation */}
        {systemPrompt && (
          <div className="tw-bg-gray-100 tw-border tw-border-gray-300 tw-rounded tw-p-4 tw-mb-4 tw-text-xs tw-font-mono tw-relative tw-overflow-x-auto">
            <button
              className="tw-absolute tw-top-2 tw-right-2 tw-bg-gray-200 tw-px-2 tw-py-1 tw-rounded tw-text-xs tw-shadow hover:tw-bg-gray-300"
              onClick={() => {navigator.clipboard.writeText(systemPrompt)}}
              title="Copier le prompt"
              type="button"
            >
              Copier
            </button>
            <pre className="tw-whitespace-pre-wrap tw-text-xs tw-font-mono tw-m-0">{systemPrompt}</pre>
          </div>
        )}
        {conversation.messages.length === 0 && !conversation.isStreaming && (
          <div className="tw-text-gray-400 tw-italic tw-text-center tw-mt-8">Aucun message pour cette conversation.</div>
        )}
        {conversation.messages.map((msg: IAChatMessage) => (
          <div
            key={msg.id}
            className={`tw-flex ${msg.role === 'user' ? 'tw-justify-end' : 'tw-justify-start'}`}
          >
            <div
              className={`tw-max-w-[70%] tw-p-3 tw-rounded-lg tw-shadow-sm tw-text-sm ${msg.role === 'user' ? 'tw-bg-blue-600 tw-text-white tw-ml-auto' : 'tw-bg-gray-200 tw-text-gray-800 tw-mr-auto'}`}
            >
              {msg.role === 'assistant' ? (
                <MarkdownRenderer content={msg.content} />
              ) : (
                <div>
                  <div>{msg.content}</div>
                  {msg.attachments && msg.attachments.length > 0 && (
                    <div className="tw-mt-2 tw-space-y-1">
                      {msg.attachments.map((attachment) => (
                        <div
                          key={attachment.id}
                          className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-bg-white tw-bg-opacity-20 tw-rounded tw-p-2"
                        >
                          <i className={`fa-solid ${attachment.type === 'pdf' ? 'fa-file-pdf' : 'fa-image'} tw-text-sm`}></i>
                          <span className="tw-flex-1 tw-truncate">{attachment.label}</span>
                          <button
                            onClick={async () => {
                              const doc = { id: attachment.id, label: attachment.label } as any;
                              const url = await getDocumentDownloadUrl(doc);
                              if (url) window.open(url, '_blank');
                            }}
                            className="tw-text-xs tw-underline hover:tw-no-underline tw-opacity-80 hover:tw-opacity-100"
                          >
                            Télécharger
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div className="tw-text-[10px] tw-text-gray-300 tw-mt-1 tw-text-right">
                {new Date(msg.createdAt).toLocaleTimeString()}
              </div>
            </div>
          </div>
        ))}
        {/* Message IA en streaming */}
        {conversation.isStreaming && (
          <div className="tw-flex tw-justify-start">
            <div className="tw-max-w-[70%] tw-p-3 tw-rounded-lg tw-shadow-sm tw-text-sm tw-bg-gray-200 tw-text-gray-800 tw-mr-auto tw-italic tw-relative">
              <MarkdownRenderer content={conversation.streamingContent || ''} />
              <span className="tw-absolute tw-right-2 tw-bottom-2 tw-animate-pulse tw-text-gray-400">...</span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      {/* Input + Pièces jointes */}
      <div className="tw-p-4 tw-border-t tw-border-gray-200 tw-bg-white tw-flex tw-flex-col tw-gap-2">
        {/* Sélecteur de documents du SAV */}
        {sav && (
          <DocumentsSelector
            savId={sav.id}
            documentsSelected={selectedDocuments}
            onChangeSelectedDocuments={setSelectedDocuments}
            labelBtn="Joindre un document du SAV"
            selectMulti={true}
            hideList={false}
          />
        )}
        {/* Input message */}
        <div className="tw-flex tw-gap-2">
          <input
            ref={inputRef}
            type="text"
            className="tw-flex-1 tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-lg focus:tw-outline-none focus:tw-border-blue-500 tw-text-sm"
            placeholder="Écrire un message..."
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleInputKeyDown}
            disabled={!gestionnaireId || isLoading}
          />
          <button
            className="tw-bg-blue-600 tw-text-white tw-px-4 tw-py-2 tw-rounded-lg tw-shadow-sm tw-font-medium hover:tw-bg-blue-700 tw-transition disabled:tw-bg-gray-300"
            onClick={handleSend}
            disabled={!gestionnaireId || !input.trim() || isLoading}
          >
            Envoyer
          </button>
        </div>
      </div>
    </div>
  );
};

export default IAConversationPage;